/**
 * PublicBooking — Public 3-step booking flow at /book/:slug (and /book/:slug/:mtype).
 * Step 1: pick meeting type (auto-skipped if URL has :mtype)
 * Step 2: pick date + slot
 * Step 3: fill invitee info + confirm
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, Clock, Warning, Sparkle, House } from "@phosphor-icons/react";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
const ymd = (d) => d.toISOString().slice(0, 10);

export default function PublicBooking() {
  const { slug, mtype } = useParams();
  const [profile, setProfile]   = useState(null);
  const [step, setStep]         = useState(1);
  const [selectedType, setType] = useState(null);
  const [slots, setSlots]       = useState([]);
  const [loadingSlots, setLS]   = useState(false);
  const [chosen, setChosen]     = useState(null);
  const [invitee, setInvitee]   = useState({ invitee_name: "", invitee_email: "", invitee_phone: "", property_address: "", notes: "" });
  const [busy, setBusy]         = useState(false);
  const [confirmed, setConfirm] = useState(null);
  const [err, setErr]           = useState(null);

  useEffect(() => {
    api.get(`/public/book/${slug}`)
      .then(r => {
        setProfile(r.data);
        if (mtype) {
          const t = (r.data.meeting_types || []).find(m => m.slug === mtype);
          if (t) { setType(t); setStep(2); }
        }
      })
      .catch(e => setErr(e?.response?.data?.detail || "Link not found"));
  }, [slug, mtype]);

  const loadSlots = useCallback(async () => {
    if (!selectedType) return;
    setLS(true);
    try {
      const { data } = await api.get(`/public/book/${slug}/slots`, {
        params: { type: selectedType.slug, days: 14 },
      });
      setSlots(data.slots || []);
    } catch { setSlots([]); }
    finally { setLS(false); }
  }, [slug, selectedType]);
  useEffect(() => { if (step === 2) loadSlots(); }, [step, loadSlots]);

  const grouped = useMemo(() => {
    const m = {};
    for (const s of slots) {
      const k = s.starts_at.slice(0, 10);
      (m[k] ||= []).push(s);
    }
    return Object.entries(m);
  }, [slots]);

  const reserve = async () => {
    if (!chosen || !invitee.invitee_name.trim() || !invitee.invitee_email.trim()) {
      toast.error("Name and email required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/book/${slug}/reserve`, {
        meeting_type_slug: selectedType.slug,
        starts_at: chosen.starts_at,
        ...invitee,
      });
      setConfirm(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Booking failed"));
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md rounded-xl border-2 border-rose-200 bg-rose-50 p-6 text-center">
        <Warning size={40} weight="fill" className="text-rose-500 mx-auto mb-2"/>
        <div className="font-black text-rose-900">{err}</div>
      </div>
    </div>
  );
  if (!profile) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  if (confirmed) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="max-w-lg bg-white rounded-2xl border-2 border-emerald-300 p-6 text-center shadow-lg">
        <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto mb-2"/>
        <div className="font-black text-2xl text-slate-900">You're booked!</div>
        <div className="mt-2 text-slate-700">
          <b>{confirmed.meeting_type_name}</b> with <b>{confirmed.with}</b>
        </div>
        <div className="mt-1 text-slate-800 font-bold text-lg">
          {fmtDate(confirmed.starts_at)} · {fmtTime(confirmed.starts_at)} – {fmtTime(confirmed.ends_at)}
        </div>
        {invitee.property_address && (
          <div className="mt-2 text-sm text-slate-500 flex items-center justify-center gap-1">
            <House weight="fill" size={12}/> {invitee.property_address}
          </div>
        )}
        <div className="mt-4 text-xs text-slate-500">A confirmation is on the way. Reply to the email to reschedule.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-slate-900 text-white p-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-[10px] font-black uppercase tracking-widest opacity-70 flex items-center gap-1">
            <Sparkle weight="fill" size={11}/> Book time with
          </div>
          <div className="font-black text-2xl">{profile.display_name}</div>
          {profile.title && <div className="text-sm opacity-80">{profile.title}</div>}
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-3">
        {step === 1 && (
          <div className="space-y-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">What kind of meeting?</div>
            {(profile.meeting_types || []).map(t => (
              <button key={t.slug} onClick={() => { setType(t); setStep(2); }}
                className="w-full text-left rounded-xl bg-white border-2 border-slate-200 hover:border-slate-900 p-3 flex items-start gap-3"
                data-testid={`book-mtype-${t.slug}`}>
                <div className="w-1.5 self-stretch rounded" style={{ background: t.color || "#0f172a" }}/>
                <div className="flex-1">
                  <div className="font-black text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.duration_min} min{t.description ? ` · ${t.description}` : ""}</div>
                </div>
                <Clock size={16} className="text-slate-400 mt-1"/>
              </button>
            ))}
          </div>
        )}
        {step === 2 && selectedType && (
          <div>
            <button onClick={() => { setStep(1); setChosen(null); }} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-1">
              <ArrowLeft size={12}/> Change type
            </button>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
              <b>{selectedType.name}</b> · {selectedType.duration_min} min · pick a slot
            </div>
            {loadingSlots ? <div className="text-sm text-slate-400 p-4">Loading…</div>
              : slots.length === 0 ? <div className="text-sm text-slate-400 p-4">No slots available in the next 14 days.</div>
              : (
                <div className="space-y-3">
                  {grouped.map(([day, items]) => (
                    <div key={day} className="rounded-xl bg-white border-2 border-slate-200 p-3">
                      <div className="text-xs font-black text-slate-900 mb-1.5">{fmtDate(day + "T00:00:00")}</div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                        {items.map(s => (
                          <button key={s.starts_at} onClick={() => { setChosen(s); setStep(3); }}
                            className="h-10 rounded-lg border-2 border-slate-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white text-xs font-bold"
                            data-testid={`book-slot-${s.starts_at}`}>
                            {fmtTime(s.starts_at)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
        {step === 3 && chosen && (
          <div className="space-y-3">
            <button onClick={() => { setChosen(null); setStep(2); }} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <ArrowLeft size={12}/> Change time
            </button>
            <div className="rounded-xl bg-white border-2 border-emerald-300 p-3">
              <div className="text-xs font-black text-emerald-900">Confirming</div>
              <div className="font-bold text-slate-900">{selectedType.name}</div>
              <div className="text-sm text-slate-600">{fmtDate(chosen.starts_at)} · {fmtTime(chosen.starts_at)}–{fmtTime(chosen.ends_at)} · with {profile.display_name}</div>
            </div>
            {["invitee_name", "invitee_email", "invitee_phone", "property_address"].map((k, i) => {
              const labels = ["Your name *", "Email *", "Phone (optional)", "Property address (optional)"];
              return (
                <div key={k}>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{labels[i]}</label>
                  <input value={invitee[k]} onChange={e => setInvitee(s => ({ ...s, [k]: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-slate-900 text-sm bg-white outline-none"
                    data-testid={`book-field-${k}`}/>
                </div>
              );
            })}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Notes (optional)</label>
              <textarea rows={2} value={invitee.notes} onChange={e => setInvitee(s => ({ ...s, notes: e.target.value }))}
                className="mt-1 w-full p-2 border-2 border-slate-300 text-sm bg-white outline-none"
                data-testid="book-field-notes"/>
            </div>
            <button onClick={reserve} disabled={busy}
              data-testid="book-confirm"
              className="w-full h-12 bg-slate-900 hover:bg-black text-white font-black uppercase tracking-widest text-sm disabled:opacity-50">
              {busy ? "Booking…" : "Confirm Booking"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
